import { useCallback, useState } from 'react';

import { SortingDirection, SortingOffersValue } from '../constants/enums';

import { usePaginationFromURL } from './usePaginationFromURL';

interface ISortingState {
  sortValue: SortingOffersValue | null;
  direction: SortingDirection;
}

interface IUseTableSortingReturn {
  sorting: ISortingState;
  updateSorting: (
    newSortValue: SortingOffersValue,
    newDirection: SortingDirection | null
  ) => void;
}

/**
 * Manages table sorting state with a 3-click cycle per column:
 * inactive -> ASC -> DESC -> cleared (null). Passing null as
 * direction clears the active sort entirely.
 *
 * @example
 * const { sorting, updateSorting } = useTableSorting(
 *   SortingOffersValue.CREATED_AT
 * );
 */
export const useTableSorting = (
  initialSortValue: SortingOffersValue
): IUseTableSortingReturn => {
  const { sortValue, direction } = usePaginationFromURL('offers');

  const [sorting, setSorting] = useState<ISortingState>({
    sortValue: sortValue || initialSortValue,
    direction: direction || SortingDirection.DESC
  });

  const updateSorting = useCallback(
    (newSortValue: SortingOffersValue, newDirection: SortingDirection | null): void => {
      if (newDirection === null) {
        setSorting({ sortValue: null, direction: SortingDirection.DESC });
        return;
      }

      setSorting({ sortValue: newSortValue, direction: newDirection });
    },
    []
  );

  return { sorting, updateSorting };
};
